import { authorizedGetRequest, postRequest } from "./httpClient";

// Agent Config API surface exposed via useContextStateManagement(). Every
// call takes practiceId explicitly, so the only thing this needs from the
// provider is the logged-in user (for user.token) - same factory pattern as
// ticketsApi.js, same call signatures as before.
export const createAgentConfigApi = ({ user }) => {
  const getAgentConfigDraft = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return authorizedGetRequest(
      user.token,
      `/api/agentConfig/draft/${resolvedPracticeId}`,
    );
  };

  const saveAgentConfigDraft = async (practiceId, bundle) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/draft/${resolvedPracticeId}`,
      { bundle: bundle || {} },
    );
  };

  const submitAgentConfigDraft = async (practiceId, bundle) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/submit/${resolvedPracticeId}`,
      { bundle: bundle || {} },
    );
  };

  const getSubmittedAgentConfig = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return authorizedGetRequest(
      user.token,
      `/api/agentConfig/submitted/${resolvedPracticeId}`,
    );
  };

  // Issue 4 (AI Suggestions Bedrock gate): generates a fresh grouped critique of the practice's
  // current submitted draft. This is a POST, not idempotent - it calls Bedrock/Anthropic and
  // resets the "viewed" flag, so only call it from an explicit user action (the AI Suggestions
  // button), never on a background timer or effect.
  const generateAgentConfigSuggestions = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/suggestions/${resolvedPracticeId}`,
      {},
    );
  };

  const getAgentConfigSuggestions = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return authorizedGetRequest(
      user.token,
      `/api/agentConfig/suggestions/${resolvedPracticeId}`,
    );
  };

  const markAgentConfigSuggestionsViewed = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/suggestions/viewed/${resolvedPracticeId}`,
      {},
    );
  };

  // Issue 5 (Publish / live-pointer / history / rollback): publishes the practice's current
  // submitted draft. The backend rejects this (409) unless AI Suggestions have been generated
  // and viewed for that exact submitted row - this call is only ever made after the Publish
  // button's own client-side gate already agrees, but the backend re-checks regardless.
  const publishAgentConfigDraft = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/publish/${resolvedPracticeId}`,
      {},
    );
  };

  // Every version this practice has ever published, newest first, plus which one is currently
  // live (via the shared ConfigLivePointers pointer) - powers the History tab's list and its
  // "LIVE" badge.
  const getAgentConfigHistory = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return authorizedGetRequest(
      user.token,
      `/api/agentConfig/history/${resolvedPracticeId}`,
    );
  };

  // Read-only: the prompt currently live for real calls, resolved regardless of whether it was
  // last published from this Agent Config page or from the legacy Prompt Hub - powers the
  // "Currently published" display so it's always accurate even before this page has ever been
  // used for a practice. Never mutates anything server-side.
  const getAgentConfigCurrentLive = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return authorizedGetRequest(
      user.token,
      `/api/agentConfig/currentLive/${resolvedPracticeId}`,
    );
  };

  // Rolls back to an older published version by cloning its content into a brand-new published
  // version and repointing the live pointer at it - never rewrites or removes the old version,
  // so History keeps growing forward.
  const rollbackAgentConfigVersion = async (practiceId, versionId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/rollback/${resolvedPracticeId}`,
      { versionId },
    );
  };

  // Issue 8 (Demo Call - in-browser mode): mints a LiveKit room + browser access token so the
  // user can talk to the practice's current SUBMITTED draft bundle directly via mic, without
  // touching the shared live pointer real calls resolve through (a concurrent real call keeps
  // using whatever is actually published). The backend 404s if nothing has been submitted yet.
  const startAgentConfigDemoCall = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/demo/${resolvedPracticeId}`,
      {},
    );
  };

  // Deletes the Demo Call's LiveKit room server-side - same "delete the room to hang up"
  // mechanism the Page Dialer uses for /api/dialerHangup - so ending a Demo Call always cleans
  // up the room even if the browser tab closes before a local room.disconnect() call lands.
  const endAgentConfigDemoCall = async (practiceId, roomName) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/demo/end/${resolvedPracticeId}`,
      { roomName },
    );
  };

  // Issue 9 (Demo Call - phone callback mode): the practice's sticky test phone number, saved
  // once via saveAgentConfigTestPhoneNumber below and reused by every subsequent "Call This
  // Number" click until changed - lets the Agent Config page show whatever is currently saved
  // (or nothing) on load.
  const getAgentConfigTestPhoneNumber = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return authorizedGetRequest(
      user.token,
      `/api/agentConfig/testPhoneNumber/${resolvedPracticeId}`,
    );
  };

  // Saves (or, on a blank value, clears) the sticky test phone number. Free-text on the client -
  // the backend normalizes/validates E.164 format and returns the error to show inline if it
  // doesn't parse.
  const saveAgentConfigTestPhoneNumber = async (practiceId, testPhoneNumber) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/testPhoneNumber/${resolvedPracticeId}`,
      { testPhoneNumber },
    );
  };

  // Places a real outbound call to the saved sticky test phone number, running the practice's
  // current SUBMITTED draft bundle - the real telephony path (same makeOutboundSipCall trunk
  // dispatch /api/dialerCall uses), not a browser/mic session, so there is no LiveKit room for
  // this browser tab to join or later tear down; the backend never touches the shared live
  // pointer real calls resolve through, so a concurrent real call for this practice is
  // unaffected. The backend 404s if nothing has been submitted yet, and 400s if no test number
  // or outbound trunk is configured for this practice.
  const startAgentConfigPhoneDemoCall = async (practiceId) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      throw new Error("PracticeId is required");
    }

    return postRequest(
      user.token,
      `/api/agentConfig/demo/phone/${resolvedPracticeId}`,
      {},
    );
  };

  return {
    getAgentConfigDraft,
    saveAgentConfigDraft,
    submitAgentConfigDraft,
    getSubmittedAgentConfig,
    generateAgentConfigSuggestions,
    getAgentConfigSuggestions,
    markAgentConfigSuggestionsViewed,
    publishAgentConfigDraft,
    getAgentConfigHistory,
    getAgentConfigCurrentLive,
    rollbackAgentConfigVersion,
    startAgentConfigDemoCall,
    endAgentConfigDemoCall,
    getAgentConfigTestPhoneNumber,
    saveAgentConfigTestPhoneNumber,
    startAgentConfigPhoneDemoCall,
  };
};
